import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";

export default function StaffMonthlyPayments() {
  const navigate = useNavigate();
  const { staffId, month, year } = useParams();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(location.state || null);
  const [salaryRecord, setSalaryRecord] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMode: "Cash",
    transactionDate: new Date().toISOString().split("T")[0],
    transactionReference: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, [staffId, month, year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch staff info if not available
      if (!staffInfo) {
        const staffResponse = await fetch(`${API_BASE_URL}/api/staff/${staffId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (staffResponse.ok) {
          const staffData = await staffResponse.json();
          setStaffInfo(staffData);
        }
      }

      // Fetch salary record for this month
      const salaryResponse = await fetch(`${API_BASE_URL}/api/staff/salary/${staffId}/${month}/${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (salaryResponse.ok) {
        const data = await salaryResponse.json();
        setSalaryRecord(data);
      }

      // Fetch payment transactions for this salary record
      const paymentsResponse = await fetch(`${API_BASE_URL}/api/staff/${staffId}/salary-payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (paymentsResponse.ok) {
        const data = await paymentsResponse.json();
        // Filter for this specific month
        const monthRecord = data.transactions?.find(r => {
          const recordMonth = r.SalaryMonth;
          return recordMonth === `${year}-${month.padStart(2, '0')}` || 
                 recordMonth.includes(`${month}`) && recordMonth.includes(`${year}`);
        });
        
        if (monthRecord) {
          setSalaryRecord(monthRecord);
          setTransactions(monthRecord.transactions || []);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    if (!salaryRecord) {
      toast.error("Salary record not found");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_BASE_URL}/api/staff/salary-payment/transaction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          salaryId: salaryRecord.SalaryId,
          amount: parseFloat(paymentForm.amount),
          paymentMode: paymentForm.paymentMode,
          transactionDate: paymentForm.transactionDate,
          transactionReference: paymentForm.transactionReference,
          notes: paymentForm.notes,
        }),
      });

      if (response.ok) {
        toast.success("Payment recorded successfully");
        setOpenDialog(false);
        setPaymentForm({
          amount: "",
          paymentMode: "Cash",
          transactionDate: new Date().toISOString().split("T")[0],
          transactionReference: "",
          notes: "",
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to record payment");
      }
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    }
  };

  const getMonthName = () => {
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  if (!salaryRecord) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 4, 
          textAlign: "center",
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)", 
          minHeight: "calc(100vh - 80px)",
          color: "#fff"
        }}>
          <Typography variant="h6" sx={{ color: "#ccc" }}>
            No salary record found for {getMonthName()}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/staff/${staffId}/payments`)}
            sx={{ mt: 2, bgcolor: "#8B7355", "&:hover": { bgcolor: "#6D5A43" } }}
          >
            Back to Salary Records
          </Button>
        </Box>
      </AppLayout>
    );
  }

  const totalPaid = transactions.reduce((sum, t) => sum + parseFloat(t.AmountPaid || 0), 0);
  const netPayable = parseFloat(salaryRecord.NetPayable || 0);
  const remaining = netPayable - totalPaid;

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: 1200, 
        mx: "auto", 
        mt: 2, 
        p: 2, 
        background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)", 
        minHeight: "calc(100vh - 80px)" 
      }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton
            onClick={() => navigate(`/staff/${staffId}/payments`)}
            sx={{
              mr: 2,
              bgcolor: "#8B7355",
              color: "white",
              "&:hover": { bgcolor: "#6D5A43" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#fff" }}>
              SALARY PAYMENTS - {getMonthName()}
            </Typography>
            {staffInfo && (
              <Typography variant="h6" sx={{ color: "#C4A574", mt: 0.5 }}>
                {staffInfo.FullName} - {staffInfo.Designation}
              </Typography>
            )}
          </Box>
          <Chip 
            label={`Staff ID: ${staffId}`} 
            sx={{ 
              bgcolor: "#C4A574", 
              color: "white", 
              fontWeight: 600,
              fontSize: "1rem",
              px: 2,
              py: 2.5
            }} 
          />
        </Box>

        {/* SALARY SUMMARY CARD */}
        <Card sx={{ mb: 3, boxShadow: 3, bgcolor: "#2a2a2a", color: "#fff" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#C4A574" }}>
              Salary Summary
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: "center", bgcolor: "#111", color: "#ccc" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Base Salary
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2" }}>
                    ₹{parseFloat(salaryRecord.BaseSalary || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: "center", bgcolor: "#111", color: "#ccc" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Deductions
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                    ₹{parseFloat(salaryRecord.TotalDeductions || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: "center", bgcolor: "#111", color: "#ccc" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Advances
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                    ₹{parseFloat(salaryRecord.TotalAdvances || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: "center", bgcolor: "#2e7d32", color: "white" }}>
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    Net Payable
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    ₹{netPayable.toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2, borderColor: "rgba(196,165,116,0.12)" }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "#ccc" }}>Total Paid</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32" }}>
                  ₹{totalPaid.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "#ccc" }}>Remaining</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: remaining > 0 ? "#d32f2f" : "#2e7d32" }}>
                  ₹{remaining.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ color: "#ccc" }}>Status</Typography>
                <Chip
                  label={salaryRecord.Status || "Pending"}
                  sx={{
                    bgcolor: salaryRecord.Status === "Paid" ? "#2e7d32" : 
                             salaryRecord.Status === "Processed" ? "#ed6c02" : "#666",
                    color: "white",
                    fontWeight: 600,
                    mt: 0.5
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* PAYMENT TRANSACTIONS */}
        <Card sx={{ boxShadow: 3, bgcolor: "#1f1f1f", color: "#fff" }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#C4A574" }}>
                Payment Transactions
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{ 
                  bgcolor: "#4caf50", 
                  "&:hover": { bgcolor: "#45a049" }
                }}
              >
                Add Payment
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: "#111", color: "#fff" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#2a2a2a" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#ccc" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#ccc" }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#ccc" }}>Payment Mode</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#ccc" }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#ccc" }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <TableRow key={transaction.TransactionId}>
                        <TableCell>
                          {new Date(transaction.PaymentDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#2e7d32" }}>
                          ₹{parseFloat(transaction.AmountPaid).toLocaleString()}
                        </TableCell>
                        <TableCell>{transaction.PaymentMode}</TableCell>
                        <TableCell>{transaction.TransactionReference || "-"}</TableCell>
                        <TableCell>{transaction.Remarks || "-"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#ccc" }}>
                        <Typography variant="body1">
                          No payment transactions yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* ADD PAYMENT DIALOG */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            color: "#fff"
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: "#C4A574", color: "white", fontWeight: 600 }}>
          Add Payment - {getMonthName()}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#2a2a2a" }}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={`Remaining: ₹${remaining.toLocaleString()}`}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "#C4A574",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B39565",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#ccc",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Mode"
                  value={paymentForm.paymentMode}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}
                  fullWidth
                  SelectProps={{ native: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "#C4A574",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B39565",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                    },
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentForm.transactionDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionDate: e.target.value })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "#C4A574",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B39565",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Transaction Reference"
                  value={paymentForm.transactionReference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                  fullWidth
                  placeholder="e.g., Cheque number, UPI transaction ID"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "#C4A574",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B39565",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this payment..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#1a1a1a",
                      color: "#fff",
                      "& fieldset": {
                        borderColor: "#C4A574",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B39565",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#C4A574",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#C4A574",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#2a2a2a" }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            variant="contained" 
            sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#45a049" } }}
          >
            Add Payment
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
